import React, {
  FC,
  forwardRef,
  useEffect,
  useRef,
} from 'react';
import classNames from 'classnames';
import { Counter } from 'components/form/fields/Counter';
import { Label } from '@collaborator/npm_modules/dist/components/form/label';
import { InputPropsType } from 'components/form/fields/types/InputPropsType';
import Inputmask from 'inputmask';
import { When } from 'react-if';
import useMergedRefs from '@restart/hooks/useMergedRefs';

type PropsType = InputPropsType & {
  error?: string;
};

const Input: FC<PropsType> = forwardRef(({
  label,
  labelProps,
  isRequired = false,
  isAvailableCounter = false,
  isWrapFormGroup = true,
  name,
  placeholder,
  value,
  type = 'text',
  error,
  formGroupProps = {},
  className = 'form-control',
  additionalClassName,
  errorAdditionalClassName,
  sizeLg,
  inputmask,
  ...props
}, forwardedRef) => {
  const refInput = useRef<HTMLInputElement>();
  const refs = useMergedRefs(forwardedRef, refInput);

  useEffect(() => {
    if (!inputmask) {
      return;
    }

    Inputmask(inputmask).mask(refInput.current);
  }, []);

  const wrapFormGroup = (content) => {
    if (isWrapFormGroup) {
      return (
        <div className="form-group" {...formGroupProps}>
          {content}
        </div>
      );
    }
    return content;
  };

  const renderLabel = () => <Label label={label} isRequired={isRequired} htmlFor={props.id || name} {...labelProps} />;

  const renderContent = (): React.ReactNode => {
    const content = (
      <>
        <When condition={type !== 'checkbox'}>
          {renderLabel()}
        </When>
        {/* @ts-ignore */}
        <input
          ref={refs}
          type={type}
          name={name}
          className={classNames(
            className,
            additionalClassName,
            { 'is-invalid': error },
            { 'form-control-lg': sizeLg },
          )}
          placeholder={placeholder}
          id={name}
          value={value}
          {...props}
        />
        <When condition={type === 'checkbox'}>
          {renderLabel()}
        </When>
        <div className={classNames('invalid-feedback', errorAdditionalClassName)}>
          {error}
        </div>
      </>
    );

    if (isAvailableCounter) {
      return (
        <div className="position-relative">
          <Counter isAvailable={isAvailableCounter} value={value} />
          {content}
        </div>
      );
    }

    return content;
  };

  return wrapFormGroup(renderContent());
});

export { Input };
